import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, CreditCard, ExternalLink, FileText, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { usePlans, useUsage, useInvoices, useCheckout, useBillingPortal, useSubscription, useSyncSubscription } from '@/hooks/useBilling';
import { PlanCard } from '@/components/billing/PlanCard';
import { UsageMeter } from '@/components/billing/UsageMeter';
import { InlineAlert } from '@/components/shared/InlineAlert';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { normalizePlanTier } from '@/types/billing';
import { getValidPlanIntent } from '@/lib/auth-intent';

/** Format an ISO 8601 date string as "Mar 1, 2026". */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type StatusVariant = 'active' | 'past_due' | 'trialing' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid';

/** Returns a Tailwind className pair (bg + text) for each subscription status. */
function statusColor(status: StatusVariant): string {
  switch (status) {
    case 'active':
      return 'bg-status-allow/15 text-status-allow border-status-allow/30';
    case 'past_due':
    case 'unpaid':
      return 'bg-status-warning/15 text-status-warning border-status-warning/30';
    case 'trialing':
      return 'bg-status-info/15 text-status-info border-status-info/30';
    case 'canceled':
    case 'incomplete':
    case 'incomplete_expired':
      return 'bg-status-deny/15 text-status-deny border-status-deny/30';
  }
}

/** Human-readable label for subscription status. */
function statusLabel(status: StatusVariant): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past Due';
    case 'trialing':
      return 'Trial';
    case 'canceled':
      return 'Canceled';
    case 'incomplete':
      return 'Incomplete';
    case 'incomplete_expired':
      return 'Expired';
    case 'unpaid':
      return 'Unpaid';
  }
}

export function UsageBillingPage() {
  const user = useAuthStore((s) => s.user);
  const currentTier = normalizePlanTier(user?.organization?.plan || 'free');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const checkoutResult = searchParams.get('checkout') as 'success' | 'canceled' | null;
  const highlightedPlan = getValidPlanIntent(searchParams);

  // Local dismiss state for checkout banners; initialise to "visible" when a
  // checkout param is present so the banner shows on first render.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const showSuccessBanner = checkoutResult === 'success' && !bannerDismissed;
  const showCanceledBanner = checkoutResult === 'canceled' && !bannerDismissed;

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    // Remove the ?checkout= param from the URL without a navigation push.
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    navigate({ search: next.toString() }, { replace: true });
  };

  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');

  const { data: plansData, isLoading: plansLoading } = usePlans();
  const { data: usageData, isLoading: usageLoading } = useUsage();
  const { data: invoicesData } = useInvoices();
  const { data: subscription, isActive: hasActiveSubscription } = useSubscription();
  const checkout = useCheckout();
  const billingPortal = useBillingPortal();
  const syncSubscription = useSyncSubscription();

  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleSelectPlan = async (planId: string, billingInterval: 'monthly' | 'yearly') => {
    try {
      setCheckoutError(null);
      const result = await checkout.mutateAsync({ plan_id: planId, billing_period: billingInterval });
      window.location.href = result.checkout_url;
    } catch {
      setCheckoutError('Could not start checkout. Please try again or contact support.');
    }
  };

  const handleManageBilling = async () => {
    try {
      setCheckoutError(null);
      const result = await billingPortal.mutateAsync({});
      window.location.href = result.portal_url;
    } catch {
      setCheckoutError('Could not open billing portal. Please try again.');
    }
  };

  const invoices = invoicesData?.data ?? [];

  const billingPeriodLabel = subscription?.billing_period === 'yearly' ? 'Yearly' : 'Monthly';

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader eyebrow="Account" title="Usage & Billing" />

      {/* ------------------------------------------------------------------ */}
      {/* Checkout success / canceled banners                                 */}
      {/* ------------------------------------------------------------------ */}
      {showSuccessBanner && (
        <InlineAlert variant="success" onDismiss={handleDismissBanner}>
          <span>Your subscription is being activated. It may take a moment to update.</span>
          <button
            onClick={() => {
              syncSubscription.mutate(undefined, {
                onSuccess: (data) => {
                  if (data.synced) {
                    toast.success(`Plan updated to ${data.plan}.`);
                  } else {
                    toast.info('Subscription is already up to date.');
                  }
                },
                onError: () => {
                  toast.error('Could not verify subscription. Please contact support if your plan looks wrong.');
                },
              });
            }}
            disabled={syncSubscription.isPending}
            className="mt-1.5 flex items-center gap-1 text-xs underline underline-offset-2 hover:no-underline disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${syncSubscription.isPending ? 'animate-spin' : ''}`} />
            {syncSubscription.isPending ? 'Checking…' : 'Plan not updated? Verify now'}
          </button>
        </InlineAlert>
      )}

      {showCanceledBanner && (
        <InlineAlert variant="warning" onDismiss={handleDismissBanner}>
          Checkout was canceled. You can try again anytime.
        </InlineAlert>
      )}

      {checkoutError && (
        <InlineAlert variant="destructive" onDismiss={() => setCheckoutError(null)}>
          {checkoutError}
        </InlineAlert>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Top row: Current Usage + Subscription side-by-side                  */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Current Usage */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Current Usage</h2>
          {usageLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : usageData ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <UsageMeter
                    used={usageData.secured_retrievals.used}
                    limit={usageData.secured_retrievals.limit}
                    label="Secured Retrievals"
                  />
                </div>
                <div className="rounded-lg border p-4">
                  <UsageMeter
                    used={usageData.connectors.used}
                    limit={usageData.connectors.limit}
                    label="Connectors"
                  />
                </div>
                <div className="rounded-lg border p-4">
                  <UsageMeter
                    used={usageData.policies.used}
                    limit={usageData.policies.limit}
                    label="Policies"
                  />
                </div>
                <div className="rounded-lg border p-4">
                  <UsageMeter
                    used={usageData.principals?.used ?? 0}
                    limit={usageData.principals?.limit ?? null}
                    label="Local users"
                    tooltip="Users added directly under Directory. Users synced from an identity provider do not count."
                  />
                </div>
                <div className="rounded-lg border p-4">
                  <UsageMeter
                    used={usageData.api_keys?.used ?? 0}
                    limit={usageData.api_keys?.limit ?? null}
                    label="API keys"
                  />
                </div>
                <div className="rounded-lg border p-4">
                  <UsageMeter
                    used={usageData.identity_providers?.used ?? 0}
                    limit={usageData.identity_providers?.limit ?? null}
                    label="Identity providers"
                    tooltip="Connected identity providers. The built-in local directory is free and does not count."
                  />
                </div>
              </div>
              {usageData.estimated_overage_cents > 0 && (
                <p className="text-sm text-status-warning">
                  Estimated overage:{' '}
                  <span className="font-semibold tabular">
                    ${(usageData.estimated_overage_cents / 100).toFixed(2)}
                  </span>{' '}
                  this period
                </p>
              )}
            </>
          ) : null}
        </div>

        {/* Subscription Details */}
        {subscription && (
          <div>
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">
                    Subscription Details
                  </CardTitle>
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusColor(subscription.status)}`}
                  >
                    {statusLabel(subscription.status)}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Plan + billing period */}
                  <div className="flex items-start gap-3">
                    <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Plan</p>
                      <p className="text-sm font-medium capitalize">
                        {subscription.plan_id ?? ''} &mdash; {billingPeriodLabel}
                      </p>
                    </div>
                  </div>

                  {/* Current period */}
                  <div className="flex items-start gap-3">
                    <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Current period</p>
                      <p className="text-sm font-medium">
                        {formatDate(subscription.current_period_start)} &ndash;{' '}
                        {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                  </div>

                  {/* Next renewal */}
                  {!subscription.cancel_at_period_end && subscription.status !== 'canceled' && (
                    <div className="flex items-start gap-3">
                      <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Next renewal</p>
                        <p className="text-sm font-medium">
                          {formatDate(subscription.current_period_end)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Cancel-at-period-end warning */}
                {subscription.cancel_at_period_end && (
                  <InlineAlert variant="warning">
                    Your subscription will cancel at the end of the billing period.
                  </InlineAlert>
                )}

                {/* Manage subscription CTA */}
                {hasActiveSubscription && (
                  <div className="pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManageBilling}
                      disabled={billingPortal.isPending}
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Manage Subscription
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Plans — full width, centered                                         */}
      {/* ------------------------------------------------------------------ */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Plans</h2>
          <div className="flex gap-2">
            <Button
              variant={interval === 'monthly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInterval('monthly')}
            >
              Monthly
            </Button>
            <Button
              variant={interval === 'yearly' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setInterval('yearly')}
            >
              Yearly
            </Button>
          </div>
        </div>
        {plansLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {plansData?.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                interval={interval}
                isCurrentPlan={plan.tier === currentTier}
                currentPlan={currentTier}
                isRecommended={plan.tier === 'growth'}
                isHighlighted={highlightedPlan === plan.tier}
                isLoading={checkout.isPending}
                onSelect={handleSelectPlan}
                showSavings
              />
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Invoice History — always rendered, with empty state                 */}
      {/* ------------------------------------------------------------------ */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Invoice History</h2>
        {invoices.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-12 text-center text-muted-foreground">
            <FileText className="h-8 w-8 opacity-40" />
            <p className="text-sm">
              No invoices yet. Invoices will appear here after your first payment.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Amount</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-right">PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0">
                    <td className="p-3">{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td className="p-3">${(inv.amount_cents / 100).toFixed(2)}</td>
                    <td className="p-3">
                      <Badge variant="outline" className="capitalize">
                        {inv.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <a
                        href={inv.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
